/**
 * The system bars' clearance, for inline styles.
 *
 * Android draws its navigation bar — the gesture pill or the three buttons — OVER the
 * WebView (targetSdk 36 is edge-to-edge and there is no opting out of it), and iOS does the
 * same with the home indicator. `env(safe-area-inset-bottom)` is how much of the bottom edge
 * the system owns; `viewport-fit=cover` in index.html is what opens those values up.
 *
 * **Anything a finger is meant to land on has to clear it.** A scrub bar, a SEND button, a
 * sheet's last row: pinned to `bottom: 0` of a full-screen surface it is drawn underneath the
 * navigation buttons, and every tap there goes to the system, not to us. That is how the
 * story video player's scrub strip became unreachable on Android (Oct 13 2026), and it is the
 * same shape of bug as the composer bar the day before.
 *
 * In CSS use `.safe-inset` / `.safe-bottom-inset` / `.pb-safe-bot` (index.css). These two are
 * for the surfaces written in inline styles — every viewer, player and composer in here.
 */
export const SAFE_B = 'env(safe-area-inset-bottom, 0px)';
export const SAFE_T = 'env(safe-area-inset-top, 0px)';

const len = (v) => (typeof v === 'number' ? `${v}px` : String(v == null ? '' : v).trim());

/**
 * `safeBottom(14)` → `calc(14px + env(safe-area-inset-bottom, 0px))`.
 * A number is px; a CSS length (including another `calc()`) is passed through. Nothing, 0 or
 * `'0px'` gives the bare inset, so the result is never `calc(0px + …)`.
 */
export function safeBottom(base = 0) {
  const b = len(base);
  if (!b || b === '0' || b === '0px') return SAFE_B;
  return `calc(${b} + ${SAFE_B})`;
}

/**
 * The same for a `padding` shorthand: `safePad('18px 16px 24px')` grows only the bottom value.
 * Takes the plain 1–4 value forms; a shorthand already carrying a function is returned
 * untouched rather than mangled, so always pass a simple one.
 */
export function safePad(padding) {
  const raw = String(padding == null ? '' : padding).trim();
  if (!raw || raw.includes('(')) return raw;
  const p = raw.split(/\s+/);
  const [t, r, b, l] = p.length === 1 ? [p[0], p[0], p[0], p[0]]
    : p.length === 2 ? [p[0], p[1], p[0], p[1]]
      : p.length === 3 ? [p[0], p[1], p[2], p[1]]
        : [p[0], p[1], p[2], p[3]];
  return `${t} ${r} ${safeBottom(b)} ${l}`;
}
